"use client"

import * as React from "react"
import { motion, useMotionValue, useTransform } from "framer-motion"
import {
  Home,
  LayoutGrid,
  Heart,
  ShoppingCart,
  User,
  MenuIcon,
  Factory,
  Truck,
  Info,
  Phone,
  HelpCircle,
  Settings,
  ArrowDown,
  History,
} from "lucide-react"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useNavigationStore } from "@/store/use-navigation-store"
import { useCartStore } from "@/store/use-cart-store"
import { useWishlistStore } from "@/store/use-wishlist-store"
import { useAuthStore } from "@/store/use-auth-store"

const MORE_LINKS = [
  { label: "Производители", page: "manufacturers", icon: Factory },
  { label: "Доставка", page: "delivery", icon: Truck },
  { label: "О нас", page: "about", icon: Info },
  { label: "Контакты", page: "contacts", icon: Phone },
  { label: "FAQ", page: "faq", icon: HelpCircle },
  { label: "История просмотров", page: "recently-viewed", icon: History },
  { label: "Администрирование", page: "admin", icon: Settings },
] as const

/* ------------------------------------------------------------------ */
/*  Tap animation wrapper                                              */
/* ------------------------------------------------------------------ */
function TapButton({
  children,
  onClick,
  className,
  ariaLabel,
}: {
  children: React.ReactNode
  onClick: () => void
  className?: string
  ariaLabel?: string
}) {
  return (
    <motion.button
      whileTap={{ scale: 0.88 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      onClick={onClick}
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </motion.button>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */

export function MobileBottomNav() {
  const navigate = useNavigationStore((s) => s.navigate)
  const currentPage = useNavigationStore((s) => s.page)
  const setCartDrawerOpen = useNavigationStore((s) => s.setCartDrawerOpen)
  const totalItems = useCartStore((s) => s.totalItems)
  const wishlistCount = useWishlistStore((s) => s.totalCount)
  const user = useAuthStore((s) => s.user)
  const [moreOpen, setMoreOpen] = React.useState(false)

  const cartCount = totalItems()
  const wishCount = wishlistCount()
  const moreLinks = user?.isAdmin
    ? MORE_LINKS
    : MORE_LINKS.filter((link) => link.page !== "admin")

  /* ---- scroll hide/show ---- */
  const [visible, setVisible] = React.useState(true)
  const lastScrollY = React.useRef(0)

  React.useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const currentY = window.scrollY
        const diff = currentY - lastScrollY.current
        if (diff > 10 && currentY > 80) {
          setVisible(false)
        } else if (diff < -5) {
          setVisible(true)
        }
        lastScrollY.current = currentY
        ticking = false
      })
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  /* ---- pull-to-refresh indicator (decorative) ---- */
  const pullY = useMotionValue(0)
  const pullOpacity = useTransform(pullY, [0, 60], [0, 1])
  const pullScale = useTransform(pullY, [0, 60], [0.5, 1])

  React.useEffect(() => {
    let startY = 0
    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY
    }
    const onTouchMove = (e: TouchEvent) => {
      if (window.scrollY > 0) {
        pullY.set(0)
        return
      }
      const diff = e.touches[0].clientY - startY
      if (diff > 0) {
        pullY.set(Math.min(diff * 0.5, 80))
      }
    }
    const onTouchEnd = () => {
      pullY.set(0)
    }
    window.addEventListener("touchstart", onTouchStart, { passive: true })
    window.addEventListener("touchmove", onTouchMove, { passive: true })
    window.addEventListener("touchend", onTouchEnd, { passive: true })
    return () => {
      window.removeEventListener("touchstart", onTouchStart)
      window.removeEventListener("touchmove", onTouchMove)
      window.removeEventListener("touchend", onTouchEnd)
    }
  }, [pullY])

  /* ---- nav items ---- */
  const navItems = [
    { page: "home", label: "Главная", icon: Home, badge: null as number | null, isCenter: false },
    { page: "catalog", label: "Каталог", icon: LayoutGrid, badge: null as number | null, isCenter: false },
    { page: "_cart", label: "Корзина", icon: ShoppingCart, badge: cartCount, isCenter: true },
    { page: "wishlist", label: "Избранное", icon: Heart, badge: wishCount, isCenter: false },
    { page: "profile", label: "Профиль", icon: User, badge: null as number | null, isCenter: false },
  ]

  const handleNav = React.useCallback(
    (page: string) => {
      if (page === "_cart") {
        setCartDrawerOpen(true)
      } else {
        navigate(page)
      }
    },
    [navigate, setCartDrawerOpen]
  )

  return (
    <>
      {/* Pull-to-refresh decorative indicator */}
      <motion.div
        className="fixed top-0 left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
        style={{ opacity: pullOpacity, scale: pullScale }}
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background/90 backdrop-blur border shadow-sm">
          <ArrowDown className="size-4 text-primary animate-bounce" />
          <span className="text-xs font-medium text-muted-foreground">Обновление...</span>
        </div>
      </motion.div>

      <motion.nav
        initial={{ y: 80 }}
        animate={{ y: visible ? 0 : 100 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background/95 backdrop-blur-xl border-t"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 80px)' }}
      >
        <div className="flex items-end justify-around px-1 pb-1 pt-1.5 h-[68px]">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = item.page === "_cart"
              ? false
              : currentPage === item.page

            if (item.isCenter) {
              /* ---- Elevated center cart button ---- */
              return (
                <TapButton
                  key={item.page}
                  onClick={() => handleNav(item.page)}
                  ariaLabel={item.label}
                  className="relative -mt-4 flex flex-col items-center justify-center"
                >
                  <motion.div
                    whileHover={{ scale: 1.08 }}
                    className="relative flex items-center justify-center rounded-full shadow-lg"
                    style={{
                      width: 56,
                      height: 56,
                      background:
                        "linear-gradient(135deg, oklch(0.75 0.15 75), oklch(0.65 0.18 55))",
                    }}
                  >
                    <ShoppingCart className="size-6 text-white" strokeWidth={2.2} />
                    {item.badge != null && item.badge > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 25 }}
                        className="absolute -top-1 -right-1 size-5 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow-sm flex"
                      >
                        {item.badge > 99 ? "99+" : item.badge}
                      </motion.div>
                    )}
                  </motion.div>
                  <span className="text-[10px] mt-1 leading-tight font-medium text-muted-foreground">
                    {item.label}
                  </span>
                </TapButton>
              )
            }

            /* ---- Regular tab ---- */
            return (
              <TapButton
                key={item.page}
                onClick={() => handleNav(item.page)}
                ariaLabel={item.label}
                className="relative flex flex-col items-center justify-center min-w-[48px] min-h-[44px] px-2"
              >
                <div className="relative">
                  <motion.div
                    animate={{
                      color: isActive
                        ? "oklch(0.65 0.18 55)"
                        : "oklch(0.5 0.01 55)",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <Icon className="size-5" fill={isActive ? "oklch(0.65 0.18 55)" : "none"} />
                  </motion.div>
                  {item.badge != null && item.badge > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                      className="absolute -top-2 -right-2.5 size-4 items-center justify-center rounded-full p-0 text-[9px] bg-red-500 text-white font-bold flex"
                    >
                      {item.badge > 99 ? "99+" : item.badge}
                    </motion.div>
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="nav-active-dot"
                      className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 size-1.5 rounded-full"
                      style={{ background: "oklch(0.65 0.18 55)" }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    />
                  )}
                </div>
                <span
                  className={`text-[10px] mt-1 leading-tight font-medium transition-colors ${
                    isActive ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {item.label}
                </span>
              </TapButton>
            )
          })}
        </div>

        {/* More button row */}
        <div className="flex justify-center pb-0.5">
          <TapButton
            onClick={() => setMoreOpen(true)}
            ariaLabel="Ещё"
            className="flex items-center gap-1 px-3 py-0.5 rounded-full text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            <MenuIcon className="size-3" />
            <span>Ещё</span>
          </TapButton>
        </div>
      </motion.nav>

      {/* More Sheet */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-xl max-h-[70vh]">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-left">Меню</SheetTitle>
          </SheetHeader>
          <Separator className="mb-2" />
          <nav className="grid grid-cols-2 gap-1">
            {moreLinks.map((link) => {
              const Icon = link.icon
              const isActive = currentPage === link.page
              return (
                <button
                  key={link.page}
                  onClick={() => {
                    navigate(link.page)
                    setMoreOpen(false)
                  }}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors min-h-[44px] ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "hover:bg-accent text-foreground"
                  }`}
                >
                  <Icon className="size-5 shrink-0" />
                  <span>{link.label}</span>
                </button>
              )
            })}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  )
}
